// src/pages/CreateRestaurantPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { createRestaurant } from '../services/firebaseService';
import { trackAdminAction } from '../services/analyticsService'; // Import analytics function
// REMOVED: import { uploadFile, validateFile, compressImage } from '../utils/uploadHelper';
// REMOVED: import { fallbackUploadLogo } from '../utils/fallbackUpload';
import { doc, updateDoc } from 'firebase/firestore';
import { db, storage, auth } from '../firebase'; // Import storage and auth instances
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage"; // Import storage functions
import { onAuthStateChanged } from 'firebase/auth'; // Import auth state listener
import { SUPPORTED_CURRENCIES } from '../config/currencies'; // Import shared currencies
import LocationPicker from '../components/LocationPicker'; // Import LocationPicker component


function CreateRestaurantPage() {
  const navigate = useNavigate();
  const [restaurantName, setRestaurantName] = useState('');
  const [slug, setSlug] = useState('');
  const [currency, setCurrency] = useState(SUPPORTED_CURRENCIES[0].code); // Default to first currency
  const [currentUser, setCurrentUser] = useState(null); // Track authenticated user
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [location, setLocation] = useState({ address: '', latitude: null, longitude: null }); // Add location state

  // Check authentication state on component mount
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
      } else {
        // Redirect to home page if not authenticated
        navigate('/');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleLogoChange = (e) => {
    if (e.target.files[0]) {
      setLogoFile(e.target.files[0]);
      setLogoPreview(URL.createObjectURL(e.target.files[0]));
    }
  };

  const generateSlug = () => {
    if (restaurantName) {
      const newSlug = restaurantName
        .toLowerCase()
        .replace(/[^\w\s]/gi, '')
        .replace(/\s+/g, '-');
      setSlug(newSlug);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Basic validation
    if (!restaurantName || !slug || !currency || !currentUser) {
      setError('Restaurant name, slug, currency are required and you must be logged in');
      return;
    }

    // Validate location
    if (!location.address || !location.latitude || !location.longitude) {
      setError('Restaurant location is required. Please select a location.');
      return;
    }

    // REMOVED: File validation logic (can be added back if needed)
    /*
    if (logoFile) {
      const validation = validateFile(logoFile, {
        maxSizeMB: 5,
        allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
      });
      
      if (validation !== true) {
        setError(validation);
        return;
      }
    }
    */
    
    try {
      setIsLoading(true);
      console.log("Creating restaurant with name:", restaurantName, "and slug:", slug);
      
      // Create restaurant document using authenticated user's email
      const newRestaurant = await createRestaurant({
        name: restaurantName,
        slug: slug,
        currency: currency, // Add currency here
        email: currentUser.email, // Use authenticated user's email
        userId: currentUser.uid, // Add user ID for better tracking
        logoUrl: null, // Initialize logoUrl as null
        location: {
          address: location.address,
          latitude: location.latitude,
          longitude: location.longitude
        } // Add location data
      });
      
      console.log("Restaurant created:", newRestaurant);
      
      // Upload logo if provided, using uploadBytesResumable directly
      if (logoFile) {
        console.log("Uploading logo file:", logoFile.name);
        setUploadProgress(0);

        const fileExtension = logoFile.name.split('.').pop();
        const uniqueFileName = `${Date.now()}_${Math.random().toString(36).substring(2, 15)}.${fileExtension}`;
        const logoPath = `restaurants/${newRestaurant.id}/logo/${uniqueFileName}`;
        const logoRef = ref(storage, logoPath);
        const metadata = { contentType: logoFile.type };

        const uploadTask = uploadBytesResumable(logoRef, logoFile, metadata);

        // Wrap upload in a promise to handle completion/error
        await new Promise((resolve, reject) => {
          uploadTask.on('state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              console.log('Upload is ' + progress + '% done');
              setUploadProgress(progress);
            },
            (error) => {
              console.error("Logo upload failed:", error);
              // Log full error details
              console.error("Full upload error object:", JSON.stringify(error, Object.getOwnPropertyNames(error)));
              setError(`Logo upload failed: ${error.code} - ${error.message}. Restaurant created without logo.`);
              setUploadProgress(0);
              reject(error); // Reject the promise on error
            },
            async () => {
              // Upload completed successfully, now get the download URL
              try {
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                console.log('File available at', downloadURL);
                // Update the restaurant document with the logo URL
                const restaurantRef = doc(db, "restaurants", newRestaurant.id);
                await updateDoc(restaurantRef, { logoUrl: downloadURL });
                console.log("Restaurant document updated with logo URL.");
                setUploadProgress(100);
                resolve(downloadURL); // Resolve the promise on success
              } catch (urlError) {
                console.error("Error getting download URL or updating doc:", urlError);
                setError(`Logo uploaded but failed to update record: ${urlError.message}`);
                reject(urlError); // Reject the promise if URL retrieval/doc update fails
              }
            }
          );
        });
        // If the promise resolved, upload was successful.
        // If it rejected, the error was handled inside the promise.

      } // End if (logoFile)
      
      // Redirect to the new dashboard page using the restaurant ID
      console.log("Redirecting to dashboard page:", `/dashboard/${newRestaurant.id}`);
      trackAdminAction('create_restaurant', { restaurantId: newRestaurant.id, restaurantName: newRestaurant.name, slug: newRestaurant.slug });
      navigate(`/dashboard/${newRestaurant.id}`);

    } catch (error) {
      // This catches errors from createRestaurant or the upload promise rejection
      console.error("Error during restaurant creation or logo upload:", error);
      let errorMessage = "Operation failed. ";
      
      if (error.message && error.message.includes("already exists")) {
        errorMessage += "This slug is already taken. Please choose another one.";
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += "Please try again.";
      }
      
      setError(errorMessage);
      setIsLoading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="container">
      <div className="header" >
        <h1 style={{ 
          textAlign: 'center',
          margin: '0',
          fontSize: '2rem',
          fontWeight: 'bold',
          width: '100%'
        }}> Create Restaurant</h1>
        <div></div> {/* Empty div for flex spacing */}
      </div>
      
      {error && (
        <div className="error-message mb-4">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="mt-4">
        <div className="form-group">
          <LocationPicker
            value={location}
            onChange={setLocation}
            required={true}
            error={error && (!location.address || !location.latitude || !location.longitude) ? 'Location is required' : null}
          />
        </div>

        <div className="form-group">
          <label htmlFor="restaurantName">Restaurant Name</label>
          <input
            type="text"
            id="restaurantName"
            className="form-control"
            value={restaurantName}
            onChange={(e) => setRestaurantName(e.target.value)}
            onBlur={generateSlug}
            placeholder="e.g. Acme Bistro"
            required
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="slug">URL ID</label>
          <div className="slug-input" style={{marginBottom:8}}>
            <input
              type="text"
              id="slug"
              className="form-control"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^\w-]/g, ''))}
              placeholder="e.g. acme-bistro"
              required
            />
          </div>
          <h5>This will be your unique URL: checkmenus.web.app/{slug}</h5>
        </div>

        <div className="form-group">
          <label htmlFor="currency">Currency</label>
          <select
            id="currency"
            className="form-control"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            required
          >
            {SUPPORTED_CURRENCIES.map((curr) => (
              <option key={curr.code} value={curr.code}>
                {curr.name} ({curr.symbol})
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Restaurant Logo (Optional)</label>
          {logoPreview ? (
            <img src={logoPreview} alt="Logo preview" className="preview-img" />
          ) : (
            <div className="upload-btn" onClick={() => document.getElementById('logoInput').click()}>
              <span>+ Upload Logo</span>
            </div>
          )}
          <input
            type="file"
            id="logoInput"
            accept="image/*"
            onChange={handleLogoChange}
            style={{ display: 'none' }}
          />
          {logoPreview && (
            <button 
              type="button" 
              className="btn btn-outline mt-2"
              onClick={() => {
                setLogoFile(null);
                setLogoPreview(null);
              }}
            >
              Remove Logo
            </button>
          )}
        </div>
        
        <div className="form-group mt-4">
          <button type="submit" className="btn" disabled={isLoading}>
            {isLoading ? 
              (uploadProgress > 0 && uploadProgress < 100 ? 
                `Uploading... ${uploadProgress.toFixed(0)}%` : 
                'Creating...') : 
              'Create Restaurant'}
          </button>
          
          {/* Show progress bar when uploading */}
          {isLoading && uploadProgress > 0 && uploadProgress < 100 && (
            <div className="upload-progress-container" style={{ marginTop: '10px', backgroundColor: '#f0f0f0', borderRadius: '4px', overflow: 'hidden' }}>
              <div 
                className="upload-progress-bar" 
                style={{ 
                  width: `${uploadProgress}%`, 
                  backgroundColor: 'var(--primary-color)', 
                  height: '8px',
                  transition: 'width 0.3s ease'
                }}
              ></div>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}

export default CreateRestaurantPage;